import { CCol, CRow } from '@coreui/react'
import { Backdrop, CircularProgress } from '@mui/material'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import _, { noop } from 'lodash'
import moment from 'moment'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { getInvoiceReportApiCallInfo } from 'src/Controllers/Invoices/invoicesApi'
import AccountRow from 'src/components/AccountRow/AccountRow'
import InvoiceList from 'src/components/InvoiceList/InvoiceList'
import InvoiceBudgetComponent from 'src/components/InvoicePageComponents/InvoiceBudgetComponent/InvoiceBudgetComponent'
import AlertComponent from 'src/components/common/AlertComponent/AlertComponent'
import env from 'src/env'
import { getHttpInit } from 'src/utils/api'
import { DEFAULT_ERROR_HANDLER, handleErrorResponse } from 'src/utils/apiUtils'
import styles from './invoices.module.scss'

/**
 * Account is considered pre bill if billing type is 1 Month"
 * If billing type is 'Only after delivery' then account is considered post bill
 */
const isPostBill = (billingType) => billingType === 'Only after delivery'
export const downloadInvoice =
  (setAlertComponent, setLoading, fruitful_account_id) =>
  async (
    fruitful_invoice_id,
    invoiceDate,
    isPreBill = false,
    isAdjustment = false,
    isMultiAccount = false,
    isAdminSnapshot = false,
    dates = { start: '', end: '' },
  ) => {
    // Get translation function
    const { i18next } = window
    const t = (key) => i18next?.t(key) || key
    try {
      setLoading(true)
      const invoiceNo = fruitful_invoice_id?.split('-')[1]
      const response = await getInvoiceReportApiCallInfo({
        invoiceNo,
        fruitful_account_id,
        isPreBill,
        isAdjustment,
        isMultiAccount,
        isAdminSnapshot,
        dates,
      })
      if (!response.ok) {
        throw new Error('Error generating PDF')
      }
      // Build the filename for the download
      let name
      if (_.isEmpty(invoiceNo) && !_.isEmpty(dates)) {
        name = `${t('invoices.multiAccount')} ${
          isAdminSnapshot
            ? t('invoices.snapshotInvoiceSummary')
            : `${t('invoices.summary')} ${dates.start.format('MMMM YYYY')}`
        }`
      } else if (isAdjustment) {
        name = `${t('invoices.priorMonthAdjustmentBreakdown')} ${invoiceDate}`
      } else {
        name = `${t('invoices.invoiceBreakdown')} ${invoiceDate}`
      }
      // Convert the response to a Blob (binary)
      const blob = await response.blob()
      // Create a URL for the blob
      const url = window.URL.createObjectURL(blob)
      // Create an invisible link element to trigger download
      const a = document.createElement('a')
      a.href = url
      a.download = `${name}.pdf` // The name of the downloaded file
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
      setAlertComponent({
        open: true,
        severity: 'success',
        message: t('messages.reportDownloaded'),
      })
    } catch (e) {
      setLoading(false)
      DEFAULT_ERROR_HANDLER(setAlertComponent)(e)
    } finally {
      setLoading(false)
    }
  }
const Invoices = ({ account, Logout }) => {
  const { t } = useTranslation()
  const {
    accountname,
    fruitful_account_id,
    status,
    totalTrees,
    date_joined,
    createdtime,
    fo_billing_period,
    budget,
  } = account
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [loading, setLoading] = useState(false)
  const [alertComponent, setAlertComponent] = useState({
    open: false,
    severity: 'warning',
    message: '',
  })
  const invoices = useQuery({
    enabled: !!fruitful_account_id,
    queryKey: ['invoices', { account: fruitful_account_id }],
    retry: false,
    retryOnMount: true,
    queryFn: async ({ queryKey }) => {
      const [, { account }] = queryKey
      const [region, account_id] = account.split('-')
      const init = getHttpInit('GET')
      const response = await fetch(
        `${env.API_URL}/api/invoices?accountId=${account_id}&region=${region}`,
        init,
      )
      if (response.ok) {
        return response.json()
      } else {
        handleErrorResponse(response, queryClient, invoices).catch(
          DEFAULT_ERROR_HANDLER(setAlertComponent),
        )
        return undefined
      }
    },
    select: (data) => {
      const invoices = [...data.Content.Result.items]
      invoices.reverse()
      return invoices
    },
  })
  const openInvoiceDetails = (invoiceId) => {
    navigate(`/invoices/${invoiceId}`)
  }
  const onClickDownload = downloadInvoice(setAlertComponent, setLoading, fruitful_account_id)
  return (
    <>
      <AccountRow
        accountName={accountname}
        accountNumber={fruitful_account_id}
        accountStatus={status}
        accountCreated={moment(date_joined || createdtime)}
        clientSince={moment(date_joined || createdtime, 'YYYY-MM-DD')}
        totalTreeContributions={totalTrees}
      />
      <h3>{t('invoices.title')}</h3>
      <CRow className={styles.subTitleTextRow}>
        <CCol>
          <h4>{t('invoices.invoicesList')}</h4>
          <div className={styles.pageSubText}>{t('invoices.invoicesListSubtext')}</div>
        </CCol>
      </CRow>
      <InvoiceList
        invoiceList={invoices.data}
        openInvoiceDetails={openInvoiceDetails}
        isAccountPostBill={isPostBill(fo_billing_period)}
        downloadInvoice={onClickDownload}
      />
      <div className={styles.infoFont}>{t('invoices.infoFont')}</div>
      <InvoiceBudgetComponent budget={budget} />
      <AlertComponent
        alertComponent={alertComponent}
        alignment={{ vertical: 'top', horizontal: 'center' }}
        setAlertComponent={setAlertComponent}
      />
      <Backdrop
        sx={{ color: '#008848', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={invoices.isFetching || loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  )
}
Invoices.defaultProps = {
  account: {},
  Logout: noop,
}
Invoices.propTypes = {
  account: PropTypes.object,
  Logout: PropTypes.func,
}
export default Invoices
